import { useToast } from "@chakra-ui/react";
import { AntiVirusStatuses } from "common";
import env from "env";
import { isInfectedFileError } from "utils/statusHelpers";
import { cloneDeep } from "lodash";
import { DisbursementDataType, PaymentKey } from "pages/Payment";
import {
  validationFileSize,
  verifyUploadBeforeTenAMToday,
} from "pages/Payment/Payment.data";
import { resetGetDocumentPrefundInterval } from "pages/Payment/Payment.helper";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  useCreateDocumentMutation,
  useDeleteDocumentMutation,
  useGetDocumentMutation,
} from "redux/services/documentRepository";
import { CreateDocumentResponse } from "redux/services/documentRepository/types";
import { useGetProfileQuery } from "redux/services/membership";
import { shouldPoll } from "utils/statusHelpers";
import useUploadPresignLink from "./useUploadPresignLink";

interface UseUploadFileProps {
  defaultDisbursementData: DisbursementDataType[];
  paymentReportFiles: any;
  selectedDisbursementDate?: Date;
  intervalPrefundRef: React.MutableRefObject<any>;
  orgId: string | undefined;
}

const usePaymentOpsUploadFile = ({
  defaultDisbursementData = [],
  paymentReportFiles,
  selectedDisbursementDate,
  intervalPrefundRef,
  orgId,
}: UseUploadFileProps) => {
  const toast = useToast();

  const { data: profileData } = useGetProfileQuery();

  const membershipOrganisation = profileData?.data?.memberships?.[0];

  const [
    getReportDocument,
    {
      data: reportDocumentDetail,
      reset: resetReportDocumentDetail,
      isLoading: isReportDocumentDetailLoading,
      error: reportDocumentDetailError,
    },
  ] = useGetDocumentMutation();

  const [
    uploadReportFile,
    {
      isSuccess: isUploadReportSuccess,
      data: uploadReportFileData,
      reset: resetUploadReportFile,
      error: uploadReportError,
    },
  ] = useCreateDocumentMutation();

  const [deleteDocument] = useDeleteDocumentMutation();

  const {
    onUploadPresignLink,
    presignError: presignReportError,
    setPresignState: setPresignReportState,
    isLoading: isLoadingReport,
    setIsLoading: setIsLoadingReport,
  } = useUploadPresignLink();

  const [disbursementData, setDisbursementData] = useState<
    DisbursementDataType[]
  >(defaultDisbursementData);

  const reportFile = useMemo(
    () =>
      disbursementData.find(
        (item) => item.key === PaymentKey.UPLOAD_DISBURSEMENT_REPORT
      )?.files?.[0],
    [disbursementData]
  );

  const getVerifyDisbursementDate = (disbursementDate?: Date) => {
    if (disbursementDate) {
      if (verifyUploadBeforeTenAMToday(disbursementDate)) {
        return true;
      } else {
        return false;
      }
    } else {
      return true;
    }
  };

  const onCallApiUploadReport = (file: File) => {
    const reader = new FileReader();
    reader.onload = async () => {
      const fileDetails = {
        orgToken: membershipOrganisation?.token,
        userId: profileData?.data?.userId,
        name: file.name,
        contentType: file.type,
        orgId,
        meta: {
          contentType: file.type,
          folderName: env.api.uploadFolderPath,
          keepOriginalFileName: "true",
          preSignedEnabled: "true",
        },
      };

      const response = (await uploadReportFile({
        ...fileDetails,
      })) as {
        data: CreateDocumentResponse;
      };

      if (response?.data?.data?.meta?.preSignedLink) {
        const presignResponse = await onUploadPresignLink(
          response?.data?.data?.meta?.preSignedLink,
          file,
          file.type,
          PaymentKey.UPLOAD_DISBURSEMENT_REPORT
        );

        if (presignResponse && response?.data?.data?.id) {
          getReportDocument({
            fetchMetaDataOnly: true,
            documentId: response?.data?.data?.id,
            orgToken: membershipOrganisation?.token,
          });
        }
      }
    };
    reader.readAsDataURL(file);
  };

  const onUploadReport = async (files: File[]) => {
    if (!files?.length) return;

    const [file] = files;
    const fileName = file.name;
    if (!getVerifyDisbursementDate(selectedDisbursementDate)) return;

    const cloned = cloneDeep(disbursementData);
    const idx = cloned.findIndex(
      (c) => c.key === PaymentKey.UPLOAD_DISBURSEMENT_REPORT
    );
    const item = cloned[idx];

    item.files[0] = { fileName };
    const isSizeValid = validationFileSize(
      file,
      item.validations.maxFileSize.value
    );

    item.isSuccess = isSizeValid;

    setDisbursementData(cloned);

    if (item.isSuccess) {
      onCallApiUploadReport(file);
    }
  };

  const checkAntiVirusReport = useCallback(async () => {
    // APEX-1050: Check for infected file error first (403 with code 031.S01.403.08)
    if (isInfectedFileError(reportDocumentDetailError)) {
      // Stop polling immediately when infected file detected
      if (intervalPrefundRef.current) {
        clearInterval(intervalPrefundRef.current);
        intervalPrefundRef.current = null;
      }
      setIsLoadingReport(false);
      // Error message will be displayed via existing error handling in component
      return;
    }

    if (!isReportDocumentDetailLoading && reportDocumentDetail) {
      const antiVirusStatus = reportDocumentDetail?.data?.antiVirusStatus;

      // Continue polling if anti-virus is scanning
      if (shouldPoll(antiVirusStatus)) {
        resetGetDocumentPrefundInterval(
          intervalPrefundRef,
          getReportDocument,
          reportDocumentDetail,
          membershipOrganisation?.token
        );
      } else {
        // Anti-virus check complete - stop polling
        clearTimeout(intervalPrefundRef.current);
        setIsLoadingReport(false);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    isReportDocumentDetailLoading,
    reportDocumentDetail,
    reportDocumentDetailError,
    getReportDocument,
    membershipOrganisation?.token,
    intervalPrefundRef,
  ]);

  const allErrorReport = useMemo(
    () => [
      ...((uploadReportError as any)?.data?.errors ?? []),
      ...((reportDocumentDetailError as any)?.data?.errors ?? []),
      ...(presignReportError?.data?.errors ?? []),
    ],
    [uploadReportError, reportDocumentDetailError, presignReportError]
  );

  const errorMessagesReport = useMemo(
    () => allErrorReport.map((err) => err.message),
    [allErrorReport]
  );

  const hasErrorReport = errorMessagesReport.length > 0;

  const handleRemoveFile = useCallback(
    (key: PaymentKey, intervalRef: { current: any }) => {
      // reset the UI checklist elements
      const newData = disbursementData.map((item) => {
        if (item.key === key) {
          return { ...item, isSuccess: false, files: [] };
        }
        return item;
      });
      setDisbursementData(newData);

      if (intervalRef.current) {
        clearTimeout(intervalRef.current);
      }

      if (
        key === PaymentKey.UPLOAD_DISBURSEMENT_REPORT &&
        uploadReportFileData?.data?.id
      ) {
        deleteDocument({
          documentId: uploadReportFileData.data.id,
        });
      }

      if (key === PaymentKey.UPLOAD_DISBURSEMENT_REPORT) {
        resetUploadReportFile();
        resetReportDocumentDetail();
        setPresignReportState?.({});
        setIsLoadingReport?.(false);
      }
    },
    [
      disbursementData,
      uploadReportFileData?.data?.id,
      deleteDocument,
      resetUploadReportFile,
      resetReportDocumentDetail,
      setPresignReportState,
      setIsLoadingReport,
    ]
  );

  useEffect(() => {
    if (paymentReportFiles) {
      setIsLoadingReport(true);
      onUploadReport(paymentReportFiles);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [paymentReportFiles]);

  useEffect(() => {
    checkAntiVirusReport();
  }, [checkAntiVirusReport]);

  useEffect(() => {
    if (isUploadReportSuccess) {
      toast({
        title: "File Uploaded Successfully",
        description: "",
        status: "success",
        duration: 9000,
        position: "top-right",
        isClosable: true,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isUploadReportSuccess]);

  return {
    reportFile,
    isLoadingReport,
    disbursementData,
    reportDocumentDetail,
    hasErrorReport,
    errorMessagesReport,
    uploadReportFileData,
    isEnableSubmit: !isLoadingReport && reportFile,
    handleRemoveFile,
    getVerifyDisbursementDate,
    onUploadReport,
  };
};

export default usePaymentOpsUploadFile;
